import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ExternalLink, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ConfirmDelete } from "@/components/admin/shared";
import { RecordsTable } from "./RecordsTable";
import { AvailabilityPanel, BookingsPanel } from "./BookingPanels";
import { OrdersPanel, ProductsPanel } from "./StorePanels";

const FEATURE_TABS = [
  ["booking", "bookings", "Bookings", BookingsPanel], ["booking", "availability", "Availability", AvailabilityPanel],
  ["store", "orders", "Orders", OrdersPanel], ["store", "products", "Products", ProductsPanel],
];

const MembersPanel = ({ appId, onChanged }) => {
  const [members, setMembers] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [withRecords, setWithRecords] = useState(false);
  const ask = (m) => { setWithRecords(false); setDeleting(m); };
  const load = useCallback(() => api.get(`/manage/apps/${appId}/members`).then((r) => setMembers(r.data)), [appId]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (m) => {
    try { await api.patch(`/manage/apps/${appId}/members/${m.id}`, { disabled: !m.disabled }); toast.success(m.disabled ? "Member enabled" : "Member disabled — they can't log in"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try {
      const r = await api.delete(`/manage/apps/${appId}/members/${deleting.id}`, { params: { delete_records: withRecords } });
      toast.success(withRecords ? `Member and ${r.data.records_deleted} entries deleted` : "Member deleted — their entries were kept");
      setDeleting(null); load(); onChanged();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="members-panel" className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="border-b border-white/5 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Joined</th><th className="px-4 py-3" /></tr></thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-white/5 text-slate-300 last:border-0">
              <td className="px-4 py-3 text-white">{m.name}{m.disabled && <span className="ml-2 rounded-full bg-red-500/10 px-2 py-0.5 text-xs text-red-300">Disabled</span>}</td>
              <td className="px-4 py-3">{m.email}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{format(new Date(m.created_at), "MMM d, yyyy")}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right">
                <button onClick={() => toggle(m)} data-testid={`member-toggle-${m.id}`} className="mr-1 rounded-full border border-white/10 px-3 py-1 text-xs text-slate-200 hover:border-cyan-400/50">{m.disabled ? "Enable" : "Disable"}</button>
                <button onClick={() => ask(m)} aria-label="Delete member" data-testid={`member-delete-${m.id}`} className="rounded-lg p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </td>
            </tr>
          ))}
          {!members.length && <tr><td colSpan={4} data-testid="members-empty" className="px-4 py-10 text-center text-slate-500">No one has signed up yet.</td></tr>}
        </tbody>
      </table>
      <ConfirmDelete target={deleting} label={deleting ? `member ${deleting.email}` : ""} onCancel={() => setDeleting(null)} onConfirm={remove}>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={withRecords} onChange={(e) => setWithRecords(e.target.checked)} data-testid="member-delete-records-checkbox" className="h-4 w-4 accent-red-500" />
          Also delete their records?
        </label>
      </ConfirmDelete>
    </div>
  );
};

export const AppDataManager = ({ appId, backTo, backLabel }) => {
  const [app, setApp] = useState(null);
  const [tab, setTab] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/manage/apps/${appId}`).then((r) => {
    setApp(r.data);
    const first = FEATURE_TABS.find(([f]) => r.data.features?.[f])?.[1];
    setTab((t) => t || first || r.data.collections[0]?.name || "members");
  })
    .catch((e) => setError(e.response?.status === 403 ? "You don't have access to this app." : formatApiErrorDetail(e.response?.data?.detail))), [appId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p data-testid="app-manager-error" className="rounded-2xl border border-red-400/30 bg-red-500/10 p-6 text-red-300">{error}</p>;
  if (!app) return <p className="text-slate-500">Loading…</p>;
  const current = app.collections.find((c) => c.name === tab);
  const feats = FEATURE_TABS.filter(([f]) => app.features?.[f]);
  const Feature = feats.find(([, k]) => k === tab)?.[3];
  const tabCls = (on) => `whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${on ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-300 hover:border-cyan-400/40"}`;
  return (
    <div data-testid="app-data-manager">
      <Link to={backTo} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-300"><ArrowLeft className="h-4 w-4" /> {backLabel}</Link>
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono-tech text-xs uppercase tracking-widest text-cyan-400">// app data</p>
          <h1 data-testid="app-manager-name" className="mt-1 text-3xl font-bold text-white">{app.name}</h1>
        </div>
        {app.published && <a href={`/sites/${app.published}`} target="_blank" rel="noreferrer" data-testid="app-manager-live-link" className="inline-flex items-center gap-1 text-sm text-cyan-300 hover:text-white">Open live app <ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
      {!app.collections.length && !feats.length ? <p className="text-slate-500">This app hasn't been built yet.</p> : (
        <>
          <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
            {feats.map(([, k, label]) => <button key={k} onClick={() => setTab(k)} data-testid={`app-tab-${k}`} className={tabCls(tab === k)}>{label}</button>)}
            {app.collections.map((c) => (
              <button key={c.name} onClick={() => setTab(c.name)} data-testid={`app-tab-${c.name}`} className={tabCls(tab === c.name)}>
                {c.label} <span className="text-slate-500">{c.count}</span>{c.pending > 0 && <span className="ml-1 rounded-full bg-amber-400/15 px-1.5 text-xs text-amber-300">{c.pending} pending</span>}
              </button>
            ))}
            <button onClick={() => setTab("members")} data-testid="app-tab-members" className={tabCls(tab === "members")}><Users className="mr-1 inline h-3.5 w-3.5" />Members <span className="text-slate-500">{app.members}</span></button>
          </div>
          {Feature ? <Feature appId={appId} /> : tab === "members" ? <MembersPanel appId={appId} onChanged={load} /> : current && <RecordsTable key={current.name} appId={appId} collection={current} onChanged={load} />}
        </>
      )}
    </div>
  );
};
